import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
interface User {
  id: number;
  email: string;
  auth_type?: 'password' | 'oauth' | 'pending_oauth';
  [key: string]: any; // 다른 필드는 선택적
}

interface AuthTypeChangeDialogProps {
  user: User | null;
  onClose: () => void;
  onSubmit: (userId: number, authType: 'password' | 'oauth' | 'pending_oauth') => Promise<void>;
}

const AuthTypeChangeDialog: React.FC<AuthTypeChangeDialogProps> = ({
  user,
  onClose,
  onSubmit,
}) => {
  const [newAuthType, setNewAuthType] = useState<'password' | 'oauth' | 'pending_oauth'>('password');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setNewAuthType(user.auth_type || 'password');
      setError('');
      setSuccess('');
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    setSuccess('');
    
    try {
      await onSubmit(user.id, newAuthType);
      setSuccess('로그인 유형이 변경되었습니다.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.detail || '로그인 유형 변경에 실패했습니다.');
    }
  };

  return (
    <Dialog open={!!user}>
      <DialogContent className="sm:max-w-[425px]" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>로그인 유형 변경</DialogTitle>
          <DialogDescription>
            {user?.email} 사용자의 로그인 유형을 변경합니다.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {error && (
            <AlertMessage 
              type="error" 
              message={error} 
              onClose={() => {}} 
            />
          )}
          {success && (
            <AlertMessage 
              type="success" 
              message={success} 
              onClose={() => {}} 
            />
          )}
          <div className="space-y-3">
            <Label>로그인 유형 선택</Label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="auth_type"
                  value="password"
                  checked={newAuthType === 'password'}
                  onChange={() => setNewAuthType('password')}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium">🔑 ID/PW 로그인</div>
                  <div className="text-sm text-gray-500">이메일과 비밀번호로 로그인</div>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="auth_type"
                  value="oauth"
                  checked={newAuthType === 'oauth'}
                  onChange={() => setNewAuthType('oauth')}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium">🔗 Google 로그인</div>
                  <div className="text-sm text-gray-500">Google 계정으로 로그인</div>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 border-yellow-300 bg-yellow-50">
                <input
                  type="radio"
                  name="auth_type"
                  value="pending_oauth"
                  checked={newAuthType === 'pending_oauth'}
                  onChange={() => setNewAuthType('pending_oauth')}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium">⏳ OAuth 전환 대기</div>
                  <div className="text-sm text-gray-500">
                    다음 로그인 시 성공한 방식으로 자동 전환<br/>
                    <span className="text-xs">(Google 성공 → OAuth, ID/PW 성공 → ID/PW)</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button onClick={handleSubmit}>
            변경
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuthTypeChangeDialog;

